import { Feature, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerColors } from '../../models/layer-colors.model';
import { GeometryTypes } from '../../models/layer-renderer.model';
import { DatasetZIndexValues } from 'src/app/modules/cop/drawing/models/dataset-zIndex.model';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { MapLabelService } from '../../label.service';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerQueryBuildService } from '../../../services/data/map-layer-query-build.service';
import { GeoJsonUtilityService } from '../../geo-json-utility.service';
import { MapLayerClusterService } from '../../cluster.service';
import { PopupService } from '../../popups';
import { API_BASE_URL } from '@app/core/constants';

const getLatLngFromGeometry = (geometry: any) => {
  let [lng, lat] = [0, 0];

  if (geometry?.length > 0) {
    if (geometry[0].type === GeometryTypes.feature) {
      [lng, lat] = geometry[0].geometry?.type === GeometryTypes.point ? geometry[0].geometry.coordinates : [0, 0];
    } else if (geometry[0].type === GeometryTypes.point) {
      [lng, lat] = geometry[0].coordinates || [0, 0];
    }
  }

  return {
    lat,
    lng
  };
};

export const SELECTED_Z_INDEX = 10000;
export class StandardFeature implements Feature {
  protected googleMapFeatures: GoogleMapFeatures;
  protected googleMap: google.maps.Map;
  protected documentHash: number = 0;
  protected geometryHash: number = 0;
  protected isVisible: boolean = false;
  protected isSelected: boolean = false;
  protected markerUrls: MarkerUrls = { normal: '', selected: '' };
  protected layerColors: LayerColors;
  protected zIndex: number = 0;
  protected mouseoutTimers = {};
  protected layerUId: string;
  protected datasetUId: string;
  protected documentUId: string;
  protected label?: string;
  protected minLabelZoomLevel: number;
  protected layerHasLabels: boolean;
  protected labelService: MapLabelService;
  protected clusterService: MapLayerClusterService;
  protected extraGeoJsonValue: string;
  protected layerMarkerAnchor: google.maps.Point;
  protected timerId: string | number | NodeJS.Timeout;
  protected popup: PopupService;
  protected canShowPopup: boolean;
  protected popupOffset: number;
  protected markerIconOptions: { size?: google.maps.Size; anchor?: google.maps.Point; scaledSize?: google.maps.Size };
  protected markerStyles: Partial<CSSStyleDeclaration>;

  constructor() {}

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.googleMap = context.googleMap;
    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;

    const selectedDocument = context.mapState.selectedDocuments[document.uid];

    this.isSelected = !!selectedDocument;
    this.isVisible = context.isDocumentVisible(layer, document);

    this.label = MapLayerQueryBuildService.getDatasetLabelFieldDisplay(document, context.labelFields);
    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.popup = context.popup;
    this.canShowPopup = context.popup?.isEnabledForLayer(layer);
    this.popupOffset = this.getPopupOffset(layer);
    this.minLabelZoomLevel = this.labelService.getLayerLabelMinZoom(layer);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let workflowColor: string = null;
    if (context.isWorkflowLayerSection) {
      workflowColor = FeatureRenderingUtilityService.getDocumentWorkflowColor(dataset, document);
    }

    const fillColor = workflowColor ?? LayerColorsService.getLayerFillColor(document.fill_color || layer.color);
    const borderColor = LayerColorsService.getLayerBorderColorFromFillColor(fillColor);
    this.layerColors = {
      fillColor,
      borderColor
    };

    const cacheBuster = !!layer?.updatedOn ? new Date(layer.updatedOn).getTime() : context.cacheBuster;
    this.markerUrls = this.buildMarkerImageUrls(layer, dataset, document, cacheBuster);
    this.layerMarkerAnchor = this.getMarkerAnchorPoint(layer);
    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];

    const geoField = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);
    const extraGeoField = FeatureRenderingUtilityService.getExtraGeometryFieldSystemName(dataset, layer);
    const showExtraGeo =
      !!extraGeoField &&
      context.mapState.geoFieldsForOptionalDisplay?.some(
        f => f.layerUId == layer.uId && f.fieldNames?.includes(extraGeoField) && (!!!f.stepUId || document.workflow_step_uid === f.stepUId)
      );

    const extraGeoJsonValue = showExtraGeo === true ? document[extraGeoField] : null;

    // build and check the document hash...if the values have not changed then
    // bail out on the rendering process.
    const newHash = this.buildDocumentHash(dataset, layer, document, this.isSelected, this.label, showExtraGeo);
    if (this.documentHash === newHash) {
      return;
    }
    this.documentHash = newHash;

    // check to see if the geometry has changed. If so then we need to remove all existing google map object
    // and redraw. If not then update the colors and icons in the case the workflow has been updated.
    const geoJsonValue = document[geoField];

    const newGeoHash = this.buildGeometryHash(geoJsonValue ?? '');

    if (newGeoHash !== this.geometryHash || this.markersNeedRefresh(context, layer, dataset, document)) {
      this.destroy();

      // get the geometry features from the standard geo field in the document
      // and then render each to the map
      this.addGoogleMapObjects(context, layer, dataset, document, geoField);

      this.geometryHash = newGeoHash;
    } else {
      // this is an update where the geometry has not changed. just update the geometry colors and marker icons
      this.updateGoogleMapObjects(context, layer, dataset, document);
    }
    if (this.extraGeoJsonValue !== extraGeoJsonValue) {
      if (!!this.extraGeoJsonValue && !!!extraGeoJsonValue) {
        this.removeExtraGoogleMapObjects(context, layer, dataset, extraGeoField);
      }
      if (!!!this.extraGeoJsonValue && !!extraGeoJsonValue) {
        this.addExtraGoogleMapObjects(context, layer, dataset, document, extraGeoField, extraGeoJsonValue);
      }
      this.extraGeoJsonValue = extraGeoJsonValue;
    }

    if (layer?.displayOptions?.historyTrackEnabled && this.isSelected) {
      this.drawHistoryTrack(selectedDocument?.__history);
    }
  }

  setVisible(): void {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
      this.clusterService.registerMarkerAdd(this.layerUId, marker);
    }
    if (this.layerHasLabels === true) {
      this.showLabel();
    }

    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(true);
      const options = poly['__data'].options;
      options.visible = true;
      poly.setOptions(options);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(true);
    }
    for (let track of this.googleMapFeatures.historyTracks) {
      track.setVisible(true);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(true);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(true);
    }
  }

  setHidden(): void {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
      const labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
      if (labelOverlay) {
        labelOverlay.setVisible(false);
      }
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(false);
      const options = poly['__data'].options;
      options.visible = false;
      poly.setOptions(options);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(false);
    }
    for (let track of this.googleMapFeatures.historyTracks) {
      track.setVisible(false);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(false);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(false);
    }

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setSelected(options?: any) {
    this.isSelected = true;

    this.setMarkersSelected();

    this.setPolygonsSelected(options);

    this.setPolylinesSelected(options);

    if (options?.historyTrackEnabled && options?.historyTrackData) {
      this.drawHistoryTrack(options.historyTrackData);
    } else {
      this.clearHistoryTrack();
    }
  }

  setUnselected() {
    this.isSelected = false;

    this.setMarkersNormal();

    this.setPolygonsNormal();

    this.setPolylinesNormal();

    this.clearHistoryTrack();
  }

  showLabel(): void {
    this.layerHasLabels = true;
    if (!!!this.label) {
      return;
    }

    for (let marker of this.googleMapFeatures.markers) {
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);

      if (!labelOverlay) {
        labelOverlay = this.labelService.createLabel(
          marker,
          this.label,
          this.layerUId,
          this.documentUId,
          {
            left: null,
            top: -84
          },
          'map-marker-label',
          10000
        );
        this.labelService.addMarkerLabel(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      }
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
    }
  }

  private updateLabel(layer: Layer, marker: any): void {
    const hasLabel = !!this.label && layer.displayOptions?.labelsVisible === true;
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
    if (hasLabel) {
      if (labelOverlay) {
        labelOverlay.setLabel(this.label);
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.showLabel();
      }
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    for (let circle of this.googleMapFeatures.circles) {
      circle.unbindAll();
      circle.setMap(null);
    }
    this.googleMapFeatures.circles = [];

    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
      google.maps.event.clearInstanceListeners(marker);
      marker.unbindAll();
      marker.setMap(null);
      marker = null;
    }
    this.googleMapFeatures.markers = [];

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.unbindAll();
      polygon.setMap(null);
    }
    this.googleMapFeatures.polygons = [];

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.unbindAll();
      polyline.setMap(null);
    }
    this.googleMapFeatures.polylines = [];

    for (let track of this.googleMapFeatures.historyTracks) {
      track.unbindAll();
      track.setMap(null);
    }
    this.googleMapFeatures.historyTracks = [];

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.unbindAll();
      rectangle.setMap(null);
    }
    this.googleMapFeatures.rectangles = [];

    for (let polygonOverlay of this.googleMapFeatures.polygonOverlays) {
      polygonOverlay.unbindAll();
      polygonOverlay.setMap(null);
    }
    this.googleMapFeatures.polygonOverlays = [];
  }

  addGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geoField: string) {
    const geoFeatures = FeatureRenderingUtilityService.getGeometryFeaturesForDisplay(dataset, layer, document);
    if (!geoFeatures) {
      return;
    }

    for (let geo of geoFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo, geoField);
    }
  }

  addExtraGoogleMapObjects(
    context: MapDrawingContext,
    layer: Layer,
    dataset: Dataset,
    document: Document,
    geoField: string,
    extraGeometryJson: string
  ) {
    let extraMapGeometry = JSON.parse(extraGeometryJson);
    const extraMapFeatures = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(extraMapGeometry);

    for (let geo of extraMapFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo, geoField);
    }
  }

  removeExtraGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, geoField: string) {
    for (let i = this.googleMapFeatures.circles.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.circles[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.circles[i].unbindAll();
        this.googleMapFeatures.circles[i].setMap(null);
        this.googleMapFeatures.circles.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.markers.length - 1; i >= 0; i--) {
      const marker = this.googleMapFeatures.markers[i];
      if (marker['__data'].geoField === geoField) {
        this.labelService.deleteMarkerLabel(this.layerUId, this.googleMapFeatures.markers[i]);
        this.clusterService.registerMarkerRemove(this.layerUId, this.googleMapFeatures.markers[i]);
        this.googleMapFeatures.markers[i].unbindAll();
        this.googleMapFeatures.markers[i].setMap(null);

        this.googleMapFeatures.markers.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.polygons.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.polygons[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.polygons[i].unbindAll();
        this.googleMapFeatures.polygons[i].setMap(null);
        this.googleMapFeatures.polygons.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.polylines.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.polylines[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.polylines[i].unbindAll();
        this.googleMapFeatures.polylines[i].setMap(null);
        this.googleMapFeatures.polylines.splice(i, 1);
      }
    }

    for (let i = this.googleMapFeatures.rectangles.length - 1; i >= 0; i--) {
      if (this.googleMapFeatures.rectangles[i]['__data'].geoField === geoField) {
        this.googleMapFeatures.rectangles[i].unbindAll();
        this.googleMapFeatures.rectangles[i].setMap(null);
        this.googleMapFeatures.rectangles.splice(i, 1);
      }
    }
  }

  addGoogleMapObject(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    if (geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry, geoField);
    } else if (geometry.type === GeometryTypes.polygon || geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry, geoField);
    } else if (geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry, geoField);
    } else if (geometry.type === GeometryTypes.feature) {
      this.addFeature(context, layer, dataset, document, geometry, geoField);
    }
  }

  protected getAdditionalMarkerData(params: {
    layer: Layer;
    dataset: Dataset;
    document: Document;
    geometry: any;
  }): Record<string, string | number | boolean> {
    return {};
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];

    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected,
        anchor: this.layerMarkerAnchor,
        ...(this.markerIconOptions ?? {})
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    const additionalData = this.getAdditionalMarkerData({ layer, dataset, document, geometry });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      fillColor: this.layerColors.fillColor,
      geoField,
      ...additionalData
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: this.popupOffset,
          layer,
          dataset,
          document,
          offsetBelow: event.domEvent.target.clientHeight / 2
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const strokeColor = this.layerColors.borderColor;
    const strokeWeight = layer.displayOptions.shapeStrokeWidth ?? 0;

    const options = {
      paths: coords,
      strokeColor: strokeColor,
      fillColor: this.layerColors.fillColor,
      strokeOpacity: layer.displayOptions.strokeOpacity ?? layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: strokeWeight,
      fillOpacity: layer.displayOptions.fillOpacity ?? layer.displayOptions.shapeOpacity ?? 0.45,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    };

    const polygon = new google.maps.Polygon(options);

    polygon['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      options: options,
      strokeColor: strokeColor,
      strokeWeight: strokeWeight,
      fillColor: this.layerColors.fillColor,
      geoField
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polygons.push(polygon);

    polygon.addListener('mouseover', () => {
      this.onPolygonMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: this.getCenterFromPolygon(polygon),
          offsetY: 80,
          layer,
          dataset,
          document
        });
      }
    });

    polygon.addListener('mouseout', () => {
      this.onPolygonMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    polygon.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    polygon.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    polygon.addListener('rightclick', event => {
      // this triggers the Measure tool "contextmenu" listener
      google.maps.event.trigger(context.googleMap, 'contextmenu', event);
    });
  }

  addPolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const zIndex = this.zIndex ?? 3000;
    const strokeWeight = layer.displayOptions.shapeStrokeWidth ?? 2;
    const strokeColor = this.layerColors.fillColor;

    const options = {
      path: geometry.coordinates?.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor,
      strokeWeight,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex
    };
    const polyline = new google.maps.Polyline(options);

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      strokeColor,
      strokeWeight,
      zIndex,
      geoField,
      options
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('mouseover', () => {
      this.onPolylineMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: this.getCenterFromPolyline(polyline),
          offsetY: 80,
          layer,
          dataset,
          document
        });
      }
    });

    polyline.addListener('mouseout', () => {
      this.onPolylineMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addMultilinePolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const zIndex = this.zIndex ?? 3000;
    const strokeWeight = layer.displayOptions.shapeStrokeWidth ?? 2;
    const strokeColor = this.layerColors.fillColor;

    const options = {
      path: geometry.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor,
      strokeWeight,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex
    };
    const polyline = new google.maps.Polyline(options);

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      strokeColor,
      strokeWeight,
      zIndex,
      geoField,
      options
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('mouseover', () => {
      this.onPolylineMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: this.getCenterFromPolyline(polyline),
          offsetY: 80,
          layer,
          dataset,
          document
        });
      }
    });

    polyline.addListener('mouseout', () => {
      this.onPolylineMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addFeature(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string): void {
    if (geometry.geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.polygon || geometry.geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry.geometry, geoField);
    } else if (geometry.geometry.type === GeometryTypes.multiLineString) {
      for (let lineString of geometry.geometry.coordinates) {
        this.addMultilinePolyline(context, layer, dataset, document, lineString, geoField);
      }
    }
  }

  markersNeedRefresh(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): boolean {
    for (let marker of this.googleMapFeatures.markers) {
      if (marker['__data'].fillColor !== this.layerColors.fillColor) {
        return true;
      }
    }

    return false;
  }

  updateGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): void {
    for (let marker of this.googleMapFeatures.markers) {
      const icon = marker.getIcon() as google.maps.Icon;
      icon.url = this.isSelected ? this.markerUrls.selected : this.markerUrls.normal;

      marker['__data'].fillColor = this.layerColors.fillColor;

      marker.setIcon(icon);

      this.clusterService.registerMarkerRemove(this.layerUId, marker);
      this.clusterService.registerMarkerAdd(this.layerUId, marker);
    }

    if (this.googleMapFeatures.markers.length) {
      this.updateLabel(layer, this.googleMapFeatures.markers[0]);
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });

      polygon['__data'] = this.getUpdatedPolyOptions(polygon['__data'], layer);
    }

    for (let circle of this.googleMapFeatures.circles) {
      circle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });

      circle['__data'] = this.getUpdatedPolyOptions(circle['__data'], layer);
    }

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setValues({
        strokeColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });

      rectangle['__data'] = this.getUpdatedPolyOptions(rectangle['__data'], layer);
    }
  }

  getUpdatedPolyOptions(polyData: any, layer: Layer) {
    return {
      ...polyData,
      options: {
        ...polyData.options,
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      },
      strokeColor: this.layerColors.borderColor,
      fillColor: this.layerColors.fillColor,
      fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
    };
  }

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    const badgeCount = this.getBadgeCount(dataset, document) ?? '';
    const backColor = this.layerColors.fillColor.replace('#', '');
    let layerIconUrl = `${API_BASE_URL}/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_${cacheBuster}.png`;
    let layerIconSelectedUrl = `${API_BASE_URL}/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_selected_${cacheBuster}.png`;
    if (layer.isEdited) {
      const iconPath = layer.iconPath?.indexOf('data:') != -1 ? '' : layer.iconPath;

      // using the "normal" state here ensures the correct offset position in the image renderer
      layerIconUrl = `${API_BASE_URL}/v1/meta/markers/sample/${layer.markerName}_${layer.markerSize}_${layer.color.replace('#', '')}_${iconPath
        .split('_')
        .join('~')}_normal_${cacheBuster}.png`;
      layerIconSelectedUrl = `${API_BASE_URL}/v1/meta/markers/sample/${layer.markerName}_${layer.markerSize}_${layer.color.replace('#', '')}_${iconPath
        .split('_')
        .join('~')}_selected_${cacheBuster}.png`;
    }

    return {
      normal: layerIconUrl,
      selected: layerIconSelectedUrl
    };
  }

  buildDocumentHash(dataset: Dataset, layer: Layer, document: Document, isSelected: boolean, label: string, showExtraGeo: boolean): number {
    const geometryFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

    const geometry = document[geometryFieldName] ?? '';
    const workflowStepUId = document.workflow_step_uid;
    const fillColor = document.fill_color ?? '';
    const borderColor = document.border_color ?? '';
    const badgeCount = this.getBadgeCount(dataset, document);

    const stringsToHash = [
      geometry,
      workflowStepUId,
      fillColor,
      borderColor,
      isSelected,
      badgeCount ?? '',
      label ?? '',
      showExtraGeo.toString()
    ];

    const hash = cyrb53(stringsToHash.join());

    return hash;
  }

  buildGeometryHash(geometry: string): number {
    const hash = cyrb53(geometry);
    return hash;
  }

  getBadgeCount(dataset: Dataset, document: Document): number | null {
    if (dataset.type !== DatasetType.Incident) {
      return null;
    }

    const field = dataset.schema?.fields.find(
      f => f.dataType === FieldDataType.RelatedParent && f.systemName?.startsWith('alerts_') && document[f.systemName] !== undefined
    );
    if (!!!field) {
      return null;
    }

    const value = document[field.systemName];

    return !isNaN(value) ? value : null;
  }

  getMarkerAnchorPoint(layer: Layer): google.maps.Point {
    if (!!this.markerIconOptions) {
      return this.markerIconOptions.anchor;
    }

    let anchor = null;

    if (layer.customMarkerImage) {
      // Align custom markers to be centered, allowing for padding/halo added by the ImageService
      let maxDimension = Math.max(layer.customMarkerImageHeight, layer.customMarkerImageWidth);
      maxDimension = maxDimension * 1.141 + 30;
      anchor = new google.maps.Point(maxDimension / 2, maxDimension / 2);
    } else if (layer.markerName?.includes('dot')) {
      switch (layer.markerSize) {
        case 'tiny':
          anchor = new google.maps.Point(18, 30);
          break;
        case 'small':
          anchor = new google.maps.Point(27, 38);
          break;
        case 'medium':
          anchor = new google.maps.Point(35, 48);
          break;
        case 'large':
          anchor = new google.maps.Point(43, 56);
          break;
      }
    } else {
      // Align marker to bottom of pin
      switch (layer.markerSize) {
        case 'tiny':
          anchor = new google.maps.Point(18, 32);
          break;
        case 'small':
          anchor = new google.maps.Point(27, 47);
          break;
        case 'medium':
          anchor = new google.maps.Point(35, 61);
          break;
        case 'large':
          anchor = new google.maps.Point(43, 76);
          break;
      }
    }

    return anchor;
  }

  getPopupOffset(layer: Layer): number {
    if (!layer) {
      return 0;
    }

    if (layer.customMarkerImage) {
      return layer.customMarkerImageHeight;
    }

    // TODO: add in sizes for custom markers from integrations

    const isDotMarkerType = layer?.markerName?.includes('dot');

    switch (layer.markerSize) {
      case 'tiny':
        return isDotMarkerType ? 65 : 62;
      case 'small':
        return isDotMarkerType ? 60 : 52;
      case 'medium':
        return isDotMarkerType ? 55 : 42;
      case 'large':
        return isDotMarkerType ? 50 : 32;
    }
  }

  getCenterFromPolygon(polygon: google.maps.Polygon): google.maps.LatLng {
    const bounds = new google.maps.LatLngBounds();
    const paths = polygon.getPath();
    paths?.forEach(p => bounds.extend(p));
    return bounds.getCenter();
  }

  getCenterFromPolyline(polyline: google.maps.Polyline): google.maps.LatLng {
    const bounds = new google.maps.LatLngBounds();
    const paths = polyline.getPath();
    paths?.forEach(p => bounds.extend(p));
    return bounds.getCenter();
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkersSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkersNormal();
        clearTimeout(that.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  onPolylineMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setPolylinesSelected();
    this.startHoverTimer();
  }

  onPolylineMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setPolylinesNormal();
        clearTimeout(this.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  onPolygonMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setPolygonsSelected();
    this.startHoverTimer();
  }

  onPolygonMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setPolygonsNormal();
        clearTimeout(this.timerId);
        that.timerId = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkersSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected,
        anchor: this.layerMarkerAnchor,
        ...(this.markerIconOptions ?? {})
      });
    }
  }

  setMarkersNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal,
        anchor: this.layerMarkerAnchor,
        ...(this.markerIconOptions ?? {})
      });
    }
  }

  setPolygonsSelected(displayOptions?: any): void {
    for (let polygon of this.googleMapFeatures.polygons) {
      const options = polygon['__data'].options;
      const origZIndex = options.zIndex;
      polygon['__data'].zIndex = origZIndex;
      options.strokeColor = '#7257FF';
      options.strokeWeight = 4;

      if (displayOptions?.isTileDocument) {
        options.fillOpacity = 0;
      }

      polygon.setOptions(options);
    }
  }

  setPolygonsNormal(): void {
    for (let polygon of this.googleMapFeatures.polygons) {
      const options = polygon['__data'].options;
      const strokeColor = polygon['__data'].strokeColor;
      const fillColor = polygon['__data'].fillColor;
      const strokeWeight = polygon['__data'].strokeWeight;
      options.strokeWeight = strokeWeight;
      options.strokeColor = strokeColor;
      options.fillColor = fillColor;
      polygon.setOptions(options);
    }
  }

  setPolylinesSelected(displayOptions?: any): void {
    for (let polyline of this.googleMapFeatures.polylines) {
      const options = polyline['__data'].options;
      const origZIndex = options.zIndex;
      polyline['__data'].zIndex = origZIndex;
      options.strokeColor = '#7257FF';
      options.strokeWeight = 4;

      polyline.setOptions(options);
    }
  }

  setPolylinesNormal(): void {
    for (let polyline of this.googleMapFeatures.polylines) {
      const options = polyline['__data'].options;
      const strokeColor = polyline['__data'].strokeColor;
      const strokeWeight = polyline['__data'].strokeWeight;
      options.strokeWeight = strokeWeight;
      options.strokeColor = strokeColor;
      polyline.setOptions(options);
    }
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const that = this;
    this.timerId = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkersNormal();
        that.setPolygonsNormal();
      }
    }, 3000);
  }

  drawHistoryTrack(historyTrackData: Document[] = []): void {
    this.clearHistoryTrack();

    const path = historyTrackData
      .map(d => {
        const parsedGeometry = JSON.parse(d.geometry);
        const geometry = GeoJsonUtilityService.getNormalizedGeometriesOrFeaturesFromGeoJson(parsedGeometry);
        return getLatLngFromGeometry(geometry);
      })
      .filter(p => p?.lat !== 0 && p?.lng !== 0);

    if (Boolean(path.length)) {
      const historyTrack = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: this.layerColors.fillColor,
        strokeOpacity: 1.0,
        strokeWeight: 2,
        map: this.googleMap
      });

      this.googleMapFeatures.historyTracks.push(historyTrack);
    }
  }

  clearHistoryTrack(): void {
    for (let track of this.googleMapFeatures?.historyTracks) {
      track.setMap(null);
      track.unbindAll();
    }
    this.googleMapFeatures.historyTracks = [];
  }
}
